import React from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { BarChart } from '@mui/x-charts/BarChart';
import { axisClasses } from '@mui/x-charts/ChartsAxis';

// -----------------------------------------------------------------------------
// Formatters
// -----------------------------------------------------------------------------
const fmtUSD = (n) =>
  (Number.isFinite(n) ? n : 0).toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

const fmtPct0 = (n) =>
  (Number.isFinite(n) ? n : 0).toLocaleString(undefined, {
    style: 'percent',
    maximumFractionDigits: 0,
  });

// -----------------------------------------------------------------------------
// Tooltip used by Revenue + Expenses charts (lists each stacked series + Total)
// -----------------------------------------------------------------------------
function AxisTooltipWithTotal({ series, dataIndex, label }) {
  // `series` is the array of visible series for this chart at the hovered category.
  const rows = (series ?? []).map((s) => ({
    key: s.id ?? s.label,
    label: typeof s.label === 'function' ? s.label('tooltip') : s.label,
    color: s.color,
    value: Array.isArray(s.data) ? Number(s.data[dataIndex] ?? 0) : 0,
  }));

  // Total for the hovered bar stack
  const total = rows.reduce((a, r) => a + (r.value || 0), 0);

  return (
    <Paper
      elevation={6}
      sx={{
        p: 1.5,
        minWidth: 200,
        bgcolor: 'background.paper',
        color: 'text.primary',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
      }}
    >
      {label ? <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div> : null}

      {rows.map((r) => (
        <div
          key={r.key}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 12,
            alignItems: 'center',
            lineHeight: 1.4,
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span
              style={{
                width: 10,
                height: 10,
                background: r.color,
                display: 'inline-block',
                borderRadius: 2,
              }}
            />
            {r.label}
          </span>
          <span>{fmtUSD(r.value)}</span>
        </div>
      ))}

      <Box sx={{ my: 1, borderTop: 1, borderColor: 'divider' }} />

      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
        <span>Total</span>
        <span>{fmtUSD(total)}</span>
      </div>
    </Paper>
  );
}

// -----------------------------------------------------------------------------
// Tooltip used by Performance chart (shows delta as a dollar KPI)
// -----------------------------------------------------------------------------
function PerformanceTooltip({ series, dataIndex, label, meta }) {
  const s = (series ?? [])[0];
  const value = Array.isArray(s?.data) ? Number(s.data[dataIndex] ?? 0) : 0;

  const metaUsed = meta ?? s?.meta ?? {};
  const actualRatio = Number(metaUsed?.actualRatio ?? 0);
  const goalRatioUsed = Number(metaUsed?.goalRatioUsed ?? 0);
  const ratioDelta = Number(metaUsed?.ratioDelta ?? 0);

  return (
    <Paper
      elevation={6}
      sx={{
        p: 1.5,
        minWidth: 320,
        bgcolor: 'background.paper',
        color: 'text.primary',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
      }}
    >
      {label ? <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div> : null}

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <span style={{ fontWeight: 700 }}>Performance improvement ($)</span>
        <span style={{ fontWeight: 900 }}>{fmtUSD(value)}</span>
      </div>

      <Box sx={{ my: 1.25, borderTop: 1, borderColor: 'divider' }} />

      {/* ✅ These are the two numbers you asked for */}
      <div style={{ fontSize: 12, lineHeight: 1.5, opacity: 0.9 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <span>Actual Ratio (Expenses / Revenue)</span>
          <span style={{ fontWeight: 700 }}>{fmtPct0(actualRatio)}</span>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <span>Goal Ratio Used</span>
          <span style={{ fontWeight: 700 }}>{fmtPct0(goalRatioUsed)}</span>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <span>Delta (Actual − Goal)</span>
          <span style={{ fontWeight: 700 }}>{fmtPct0(ratioDelta)}</span>
        </div>
      </div>

      <div style={{ marginTop: 8, fontSize: 12, opacity: 0.8 }}>
        Formula: <strong>Total Revenue × (Actual − Goal)</strong>
      </div>
    </Paper>
  );
}

// -----------------------------------------------------------------------------
// Chart categories + colors
// -----------------------------------------------------------------------------
const REVENUE_CATEGORIES = ['BRONZE', 'GOLD', 'PLATINUM', 'SILVER'];

const CATEGORY_COLORS = {
  GOLD: '#FFD700',
  SILVER: '#C0C0C0',
  BRONZE: '#CD7F32',
  PLATINUM: '#E5E4E2',
};

const EXPENSE_COLORS = {
  Expenses: '#1976d2',
  Base: '#4caf50',
  Commissions: '#9c27b0',
  Bonus: '#ff9800',
};

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------
export default function CommissionBarChart({ data, rep, year, yMax, compact, goalRatio }) {
  const {
    revenueSeries: revSeries,
    expenseSeries: expSeries,
    perfSeries,
    perfAxisMax,
    subtitle,
  } = React.useMemo(() => {
    // -------------------------------------------------------------------------
    // 1) Normalize input rows
    // -------------------------------------------------------------------------
    // Prevents crashes if backend returns null/undefined during load.
    const rows = Array.isArray(data) ? data : [];

    // -------------------------------------------------------------------------
    // 2) Resolve goal_ratio (goal expense ratio) — use GOLD category if present
    // -------------------------------------------------------------------------
    // Find the row for GOLD category
    const goldRow = Array.isArray(rows)
      ? rows.find((r) => (r.category || '').toUpperCase() === 'GOLD')
      : null;
    const goldGoalRatio = goldRow && goldRow.goalRatio != null ? Number(goldRow.goalRatio) : null;
    let goalRatioUsed;
    if (Number.isFinite(goldGoalRatio)) {
      goalRatioUsed = goldGoalRatio;
    } else if (Number.isFinite(Number(goalRatio))) {
      goalRatioUsed = Number(goalRatio);
    } else {
      // fallback: first row
      const goalRatioFromRows = Number(rows?.[0]?.goalRatio);
      goalRatioUsed = Number.isFinite(goalRatioFromRows) ? goalRatioFromRows : 0;
    }

    // -------------------------------------------------------------------------
    // 3) Aggregate revenue by category and compute totals used by all charts
    // -------------------------------------------------------------------------
    const revSum = REVENUE_CATEGORIES.reduce((acc, cat) => {
      acc[cat] = 0;
      return acc;
    }, {});

    // Global totals (used in expense stack and performance)
    let totalNet = 0; // Total Revenue
    let totalBase = 0; // Base comp
    let totalExp = 0; // Expenses
    let totalCom = 0; // Commissions
    let totalBon = 0; // Bonus

    rows.forEach((row) => {
      const cat = (row.category || '').toUpperCase();

      // Defensive numeric coercion
      const net = Number(row.total_net_sales) || 0;
      const bas = Number(row.total_base) || 0;
      const exp = Number(row.total_expenses) || 0;
      const com = Number(row.total_commissions) || 0;
      const bon = Number(row.total_bonus) || 0;

      // Revenue by category (left chart)
      if (Object.prototype.hasOwnProperty.call(revSum, cat)) {
        revSum[cat] += net;
      }

      // Totals (middle + right charts)
      totalNet += net;
      totalBase += bas;
      totalExp += exp;
      totalCom += com;
      totalBon += bon;
    });

    // Total cost burden (the full stack from the Expenses chart)
    const totalExpenseStack = totalBase + totalExp + totalCom + totalBon;

    // -------------------------------------------------------------------------
    // 4) PERFORMANCE MATH (ratio-based, converted into a dollar value)
    // -------------------------------------------------------------------------
    // Actual expense ratio achieved:
    //   actualRatio = totalCost / totalRevenue
    //
    // Goal ratio:
    //   goalRatioUsed = target expense ratio (decimal)
    //
    // Delta (IMPORTANT SIGN):
    //   ratioDelta = actualRatio - goalRatioUsed
    //
    // Dollar impact:
    //   performanceImprovement = totalRevenue * ratioDelta
    //
    // Interpretation:
    //   - If actualRatio > goalRatio => ratioDelta positive => bar positive
    //     BUT user asked: negative if ratio - goal is negative.
    //     (Our formula already does that; sign is strictly (actual - goal).)
    //
    // NOTE on semantics:
    //   If you want "better than goal = positive", you may prefer:
    //      ratioDelta = goalRatioUsed - actualRatio
    //   But per your request: "ratio - goal" is the delta.
    //
    const actualRatio = totalNet > 0 ? (totalNet - totalExpenseStack) / totalNet : 0;
    const ratioDelta = actualRatio - goalRatioUsed;

    // Convert the ratio delta to a dollar figure (centered on 0)
    const performanceImprovement = totalNet * ratioDelta;

    // Color: positive (above) vs negative (below) relative to 0
    // Per your request:
    //   - negative dollars => red downward
    //   - positive dollars => green upward
    const performanceColor = performanceImprovement >= 0 ? '#2e7d32' : '#d32f2f';

    // -------------------------------------------------------------------------
    // 5) Build series objects for each chart
    // -------------------------------------------------------------------------
    const revenueSeries = REVENUE_CATEGORIES.map((cat) => ({
      label: cat,
      data: [revSum[cat]],
      stack: 'Revenue',
      color: CATEGORY_COLORS[cat],
    }));

    const expenseSeries = [
      { label: 'Base', data: [totalBase], stack: 'Expenses', color: EXPENSE_COLORS.Base },
      { label: 'Expenses', data: [totalExp], stack: 'Expenses', color: EXPENSE_COLORS.Expenses },
      {
        label: 'Commissions',
        data: [totalCom],
        stack: 'Expenses',
        color: EXPENSE_COLORS.Commissions,
      },
      { label: 'Bonus', data: [totalBon], stack: 'Expenses', color: EXPENSE_COLORS.Bonus },
    ];

    const perfSeriesData = [
      {
        label: 'Performance',
        data: [performanceImprovement],
        color: performanceColor,
        meta: {
          actualRatio,
          goalRatioUsed,
          ratioDelta,
        },
      },
    ];

    // -------------------------------------------------------------------------
    // 6) Performance chart y-axis range (symmetric around 0)
    // -------------------------------------------------------------------------
    // We want a diverging bar chart centered on 0. So we make min/max symmetrical.
    const absPerf = Math.abs(performanceImprovement);
    const perfAxisMaxValue = Math.max(1, Math.ceil(absPerf * 1.15)); // headroom, prevents 0-range axis

    // -------------------------------------------------------------------------
    // 7) Subtitle info (helpful context on the card)
    // -------------------------------------------------------------------------
    // Percent shown here is cost burden as a percent of revenue
    const pct = totalNet > 0 ? Math.round((totalExpenseStack / totalNet) * 100) : 0;

    const subtitleText = `${pct}% — Expenses, Base, Commissions, Bonus • Performance Goal: ${fmtPct0(goalRatioUsed)} • Actual: ${fmtPct0(actualRatio)}`;

    // -------------------------------------------------------------------------
    // 8) Optional debugging: uncomment to inspect calculations
    // -------------------------------------------------------------------------
    // console.table({
    //   rep,
    //   year,
    //   totalNet,
    //   totalExpenseStack,
    //   goalRatioUsed,
    //   actualRatio,
    //   ratioDelta,
    //   performanceImprovement,
    // });

    return {
      revenueSeries,
      expenseSeries,
      perfSeries: perfSeriesData,
      perfAxisMax: perfAxisMaxValue,
      subtitle: subtitleText,
      // debugging
      perfMeta: {
        actualRatio,
        goalRatioUsed,
        ratioDelta,
      },
    };
  }, [data, goalRatio]);

  return (
    <Paper elevation={3} sx={{ p: 2, mt: 2 }}>
      <Typography variant="h6" align="center">
        {rep} — Territory Performance for {year}
      </Typography>
      <Typography variant="body2" align="center" gutterBottom>
        {subtitle}
      </Typography>

      {/* 3 charts: Revenue | Expenses | Performance */}
      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
        {/* -------------------------------------------------------------------
            Revenue Chart (left)
           ------------------------------------------------------------------- */}
        <Box sx={{ width: compact ? 240 : 400, height: compact ? 340 : 500 }}>
          <Typography variant="subtitle2" align="center">
            Revenue
          </Typography>
          <BarChart
            series={revSeries}
            xAxis={[{ data: ['Revenue'], scaleType: 'band' }]}
            yAxis={[{ min: 0, max: yMax }]}
            margin={{ top: 20, bottom: 100, left: 80, right: 10 }}
            seriesProps={{ thickness: 40 }}
            tooltip={{ trigger: 'axis', axisContent: AxisTooltipWithTotal }}
            slotProps={{
              legend: {
                position: { horizontal: 'center', vertical: 'bottom' },
                direction: 'row',
                padding: 10,
              },
            }}
          />
        </Box>

        {/* -------------------------------------------------------------------
            Expenses Chart (middle)
           ------------------------------------------------------------------- */}
        <Box sx={{ width: compact ? 240 : 400, height: compact ? 340 : 500 }}>
          <Typography variant="subtitle2" align="center">
            Expenses
          </Typography>
          <BarChart
            series={expSeries}
            xAxis={[{ data: ['Expenses'], scaleType: 'band' }]}
            yAxis={[
              {
                min: 0,
                max: yMax,
                axisId: 'expenses',
                // Hide ticks/labels so only the Revenue axis shows actual tick labels.
                // Keeps the overall card cleaner while still sharing the same yMax.
                disableTicks: true,
                tickSize: 0,
                tickMargin: 0,
                hideTooltip: true, // affects axis tick tooltip only
                tickLabelProps: { style: { display: 'none' } },
              },
            ]}
            margin={{ top: 20, bottom: 100, right: 30, left: 0 }}
            tooltip={{ trigger: 'axis', axisContent: AxisTooltipWithTotal }}
            slotProps={{
              legend: {
                position: { horizontal: 'center', vertical: 'bottom' },
                direction: 'row',
                padding: 10,
              },
            }}
          />
        </Box>

        {/* -------------------------------------------------------------------
            Performance Chart (right)
            Diverging bar centered at 0
           ------------------------------------------------------------------- */}
        <Box sx={{ width: compact ? 90 : 110, height: compact ? 340 : 500 }}>
          <Typography variant="subtitle2" align="center">
            Performance vs Goal
          </Typography>

          {/* This is a single bar that can go up or down based on sign */}
          <BarChart
            series={perfSeries}
            xAxis={[{ data: ['Performance'], scaleType: 'band' }]}
            yAxis={[
              {
                // Symmetric min/max creates the "box plot" feel centered at 0
                min: -perfAxisMax,
                max: perfAxisMax,
                // Hide tickslabels so only the performance axis shows actual tick labels.
                // Keeps the overall card cleaner while still sharing the same yMax.
                disableTicks: true,
                tickSize: 10,
                tickMargin: 10,
                valueFormatter: (v) =>
                  Intl.NumberFormat(undefined, {
                    notation: 'compact',
                    maximumFractionDigits: 0,
                  })
                    .format(v)
                    .toLowerCase(),
                tickLabelStyle: { display: 'inline' }, // (newer API)
                // axisLine: { display: 'none' },       //  hides the vertical axis line
              },
            ]}
            // Move left-axis tick labels slightly inside the plot area to avoid clipping
            sx={{
              [`& .${axisClasses.left} .${axisClasses.tickLabel}`]: {
                transform: 'translateX(6px)',
                textAnchor: 'start',
              },
            }}
            margin={{ top: 20, bottom: 100, right: 30, left: 10 }}
            seriesProps={{ thickness: 40 }}
            tooltip={{
              trigger: 'axis',
              axisContent: PerformanceTooltip,
            }}
            slotProps={{
              legend: { hidden: true }, // single KPI bar doesn't need a legend
            }}
          />
        </Box>
      </Box>
    </Paper>
  );
}

CommissionBarChart.propTypes = {
  data: PropTypes.array.isRequired,
  rep: PropTypes.string.isRequired,
  year: PropTypes.number.isRequired,
  yMax: PropTypes.number.isRequired,
  compact: PropTypes.bool,

  // Recommended: pass this from rep_reg_performance.goal_ratio (decimal, e.g. 0.32)
  // If omitted, component will attempt to read rows[0].goal_ratio.
  goalRatio: PropTypes.number,
};
